import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import MDInput from 'components/MDInput';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDSelect from 'components/MDSelect';

import { FieldCard } from '../ui/FieldCard';
import { PlanBuilder } from './plan/PlanBuilder';

import { usePlans } from 'sectors/pilates/settings/plans/hooks/usePlans';
import { useActivities } from 'sectors/pilates/settings/activities/hooks/useActivities';
import MDButton from 'components/MDButton';
import { money } from 'sectors/pilates/members/components/paymentReceive/ui';

const CUSTOM_VALUE = '__CUSTOM__';
const CREATE_VALUE = '__CREATE__';

const stopSelectMouseDown = (e) => {
  e.preventDefault();
  e.stopPropagation();
};
const stopSelectClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  e.currentTarget?.blur?.();
};

function planLabel(p) {
  const price = money(p?.priceTry);
  const cap = p?.classCapacity != null ? p.classCapacity : 1;
  return `${p?.name || '-'} • ${price} • Kap: ${cap}`;
}

function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * ✅ PROF PlanPickerCard (prod)
 *
 * value: library planId (number|string) bekler.
 * Eğer sende edit-detail'de planRefId (uuid) dönüyorsa ve value'ya uuid basıyorsan,
 * out-of-range olmaması için safeValue ""'a düşer. (PlanSnapshot fallback ile yine gösterir.)
 *
 * fallbackPlanSnapshot: edit ekranında plan listesi henüz gelmeden plan detayını göstermek için.
 */
export function PlanPickerCard({
  value,
  onChangeValue,
  onChangePlanPayload,

  customPlanPayload,
  onChangeCustomPlanPayload,

  agreedPriceTry,
  onChangeAgreedPriceTry,

  fallbackPlanSnapshot = null,
  open = true,
  hideAgreedPrice = false,
}) {
  const theme = useMuiTheme();

  const plans = usePlans({ open });
  const acts = useActivities({ open });

  const savedPlans = React.useMemo(
    () => (plans.items || []).filter((p) => p?.active !== false),
    [plans.items]
  );

  const activeActivities = React.useMemo(
    () => (acts.items || []).filter((a) => a?.active !== false),
    [acts.items]
  );

  const actNameById = React.useMemo(() => {
    const map = new Map();
    (activeActivities || []).forEach((a) => map.set(String(a.id), a.name));
    return map;
  }, [activeActivities]);

  const labelSx = React.useMemo(
    () => ({
      color: 'text.primary',
      opacity: 0.72,
      fontWeight: 700,
      letterSpacing: '0.02em',
    }),
    []
  );

  const itemLabel = React.useCallback(
    (it) => {
      const name =
        it?.activityName ||
        actNameById.get(String(it?.activityId)) ||
        (it?.activityId ? `Faaliyet #${it.activityId}` : 'Faaliyet');

      const sessions = it?.sessions ?? 0;
      const makeUp = it?.makeUp ?? 0;

      // backend snapshot: giftBool + giftCount
      const giftCount = Number(it?.giftCount ?? 0);
      const giftBool = !!it?.giftBool;
      const gift = giftBool || giftCount > 0 ? ` • hediye${giftCount ? `:${giftCount}` : ''}` : '';

      return `${name} — ${sessions} seans / ${makeUp} telafi${gift}`;
    },
    [actNameById]
  );

  const [builderOpen, setBuilderOpen] = React.useState(false);
  const [builderMode, setBuilderMode] = React.useState('CREATE'); // CREATE|CUSTOM|EDIT
  const [editingPlanId, setEditingPlanId] = React.useState(null);

  const [touchedPrice, setTouchedPrice] = React.useState(false);

  // ✅ out-of-range fix: options yokken veya value listede yokken select="" olur
  const safeValue = React.useMemo(() => {
    const v = value == null ? '' : String(value);
    if (!v) return '';
    if (v === CUSTOM_VALUE || v === CREATE_VALUE) return v;

    if (!savedPlans?.length) return ''; // daha yüklenmedi
    const ok = savedPlans.some((p) => String(p.id) === v);
    return ok ? v : '';
  }, [value, savedPlans]);

  const selectedPlan = React.useMemo(() => {
    if (!safeValue || safeValue === CUSTOM_VALUE || safeValue === CREATE_VALUE) return null;
    return savedPlans.find((p) => String(p.id) === String(safeValue)) || null;
  }, [savedPlans, safeValue]);

  // ✅ plan payload üret (backend snapshot formatı)
  const buildPayloadFromLibraryPlan = React.useCallback(
    (p) => {
      const items = Array.isArray(p?.items) ? p.items : [];
      return {
        name: p?.name || '',
        priceTry: Number(p?.priceTry ?? 0),
        classCapacity: Number(p?.classCapacity ?? 1),
        items: items
          .map((it) => {
            const activityName = (
              it?.activityName ||
              it?.name ||
              actNameById.get(String(it?.activityId)) ||
              ''
            ).trim();
            if (!activityName) return null;

            return {
              activityId: it?.activityId ?? null,
              activityName,
              sessions: Number(it?.sessions ?? 0),
              makeUp: Number(it?.makeUp ?? 0),
              giftBool: !!(it?.giftBool ?? it?.gift),
              giftCount: Number(it?.giftCount ?? it?.totalGift ?? 0),
            };
          })
          .filter(Boolean),
      };
    },
    [actNameById]
  );
  const agreedIsEmpty = React.useMemo(() => {
    const n = numOrNull(agreedPriceTry);
    return n == null || n <= 0;
  }, [agreedPriceTry]);
  // ✅ select value değişimlerinde doğru aksiyonlar
  React.useEffect(() => {
    // 1) Builder tetikleri
    if (safeValue === CREATE_VALUE) {
      setBuilderOpen(true);
      setBuilderMode('CREATE');
      setEditingPlanId(null);
      return;
    }
    if (safeValue === CUSTOM_VALUE) {
      setBuilderOpen(true);
      setBuilderMode('CUSTOM');
      setEditingPlanId(null);
      return;
    }

    // 2) Library seçiliyse payload setle
    if (selectedPlan) {
      onChangeCustomPlanPayload?.(null);

      const payload = buildPayloadFromLibraryPlan(selectedPlan);
      onChangePlanPayload?.(payload);

      if (!touchedPrice && agreedIsEmpty) onChangeAgreedPriceTry?.(payload.priceTry ?? 0);
      return;
    }

    // 3) Select boş ama custom var
    if (!safeValue && customPlanPayload) {
      onChangePlanPayload?.(customPlanPayload);
      if (!touchedPrice && agreedIsEmpty) onChangeAgreedPriceTry?.(customPlanPayload.priceTry ?? 0);
      return;
    }

    // 4) hiçbiri yok
    onChangePlanPayload?.(null);
    if (!touchedPrice && agreedIsEmpty) onChangeAgreedPriceTry?.('');
  }, [
    safeValue,
    selectedPlan,
    customPlanPayload,
    touchedPrice,
    agreedIsEmpty,
    onChangePlanPayload,
    onChangeCustomPlanPayload,
    onChangeAgreedPriceTry,
    buildPayloadFromLibraryPlan,
  ]);

  const closeBuilder = React.useCallback(() => {
    setBuilderOpen(false);
    if (safeValue === CREATE_VALUE || safeValue === CUSTOM_VALUE) onChangeValue?.('');
  }, [safeValue, onChangeValue]);

  const openEdit = React.useCallback((planId) => {
    setBuilderOpen(true);
    setBuilderMode('EDIT');
    setEditingPlanId(planId);
  }, []);

  const setPlanActive = React.useCallback(
    async (planId, active) => {
      await plans.setActive(planId, active);
      await plans.fetchList();

      // eğer pasife alınan plan seçiliyse seçimi temizle
      if (String(safeValue) === String(planId)) {
        onChangeValue?.('');
        onChangePlanPayload?.(customPlanPayload ?? null);
        onChangeAgreedPriceTry?.('');
      }
    },
    [
      plans,
      safeValue,
      onChangeValue,
      onChangePlanPayload,
      onChangeAgreedPriceTry,
      customPlanPayload,
    ]
  );

  const softDialogPaper = React.useMemo(
    () => ({
      borderRadius: '20px',
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[10],
      background:
        theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, rgba(26,28,36,.92), rgba(18,20,26,.96))'
          : 'linear-gradient(180deg, rgba(255,255,255,.96), rgba(250,250,252,.98))',
    }),
    [theme]
  );

  const handleBuilderDone = React.useCallback(
    (res) => {
      setBuilderOpen(false);

      if (res?.type === 'LIBRARY') {
        onChangeCustomPlanPayload?.(null);
        onChangeValue?.(res.planId || '');
        onChangePlanPayload?.(res.payload || null);
        return;
      }

      if (res?.type === 'CUSTOM') {
        onChangeValue?.('');
        onChangeCustomPlanPayload?.(res.payload || null);
        onChangePlanPayload?.(res.payload || null);
        return;
      }

      if (res?.type === 'LIBRARY_EDITED') {
        onChangeCustomPlanPayload?.(null);
        if (res.planId) onChangeValue?.(res.planId);
        onChangePlanPayload?.(res.payload || null);
      }
    },
    [onChangeCustomPlanPayload, onChangeValue, onChangePlanPayload]
  );

  const summaryStyle = React.useMemo(
    () => ({
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '18px',
      padding: '12px 12px',
      background: 'linear-gradient(180deg, rgba(0,150,136,.10), rgba(0,150,136,.03))',
      boxShadow: theme.shadows[1],
    }),
    [theme]
  );

  const summaryLine = (label, valueText) => (
    <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <MDTypography variant="caption" sx={labelSx}>
        {label}
      </MDTypography>
      <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'text.primary' }}>
        {valueText}
      </MDTypography>
    </MDBox>
  );

  // ✅ seçili özet: library/custom yoksa edit'teki snapshot fallback
  const selectedSummary = React.useMemo(() => {
    if (selectedPlan) return { type: 'LIBRARY', plan: selectedPlan };
    if (!safeValue && customPlanPayload) return { type: 'CUSTOM', plan: customPlanPayload };
    if (!safeValue && !customPlanPayload && fallbackPlanSnapshot) {
      return { type: 'SNAPSHOT', plan: fallbackPlanSnapshot };
    }
    return null;
  }, [selectedPlan, safeValue, customPlanPayload, fallbackPlanSnapshot]);
  const hasSelectedPlan = Boolean(selectedSummary?.plan);
  React.useEffect(() => {
    if (!hasSelectedPlan) {
      setTouchedPrice(false);
      onChangeAgreedPriceTry?.('');
    }
  }, [hasSelectedPlan, onChangeAgreedPriceTry]);
  const packagePriceTry = React.useMemo(() => {
    if (selectedPlan) return Number(selectedPlan.priceTry ?? 0);
    if (!safeValue && customPlanPayload) return Number(customPlanPayload.priceTry ?? 0);
    if (fallbackPlanSnapshot) return Number(fallbackPlanSnapshot.priceTry ?? 0);
    return null;
  }, [selectedPlan, safeValue, customPlanPayload, fallbackPlanSnapshot]);

  const agreedNum = numOrNull(agreedPriceTry);
  const discountPct = React.useMemo(() => {
    if (packagePriceTry == null || packagePriceTry <= 0) return null;
    if (agreedNum == null) return null;
    return Math.round(((packagePriceTry - agreedNum) / packagePriceTry) * 100);
  }, [packagePriceTry, agreedNum]);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingPlan, setPendingPlan] = React.useState(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);

  const askDeactivate = React.useCallback((p) => {
    setPendingPlan({ id: p.id, name: p.name });
    setConfirmOpen(true);
  }, []);

  const closeConfirm = React.useCallback(() => {
    if (confirmBusy) return;
    setConfirmOpen(false);
    setPendingPlan(null);
  }, [confirmBusy]);

  const doDeactivate = React.useCallback(async () => {
    if (!pendingPlan?.id) return;
    setConfirmBusy(true);
    try {
      await setPlanActive(pendingPlan.id, false);
    } finally {
      setConfirmBusy(false);
      setConfirmOpen(false);
      setPendingPlan(null);
    }
  }, [pendingPlan, setPlanActive]);

  return (
    <FieldCard
      icon="redeem"
      tone="violet"
      title="Paket Seçimi"
      subtitle="Mevcut Paketi varsa seçin. Zorunlu Değil"
    >
      <MDBox sx={{ display: 'grid', gap: { xs: 0.9, sm: 1.1 } }}>
        <MDTypography variant="caption" sx={labelSx}>
          Paket
        </MDTypography>

        <MDSelect
          value={safeValue}
          onChange={(e) => onChangeValue?.(e.target.value)}
          renderValue={(v) => {
            if (!v) return customPlanPayload ? 'Özel Plan (seçili)' : 'Paket seç';
            if (v === CUSTOM_VALUE) return 'Özel Plan';
            if (v === CREATE_VALUE) return 'Plan Oluştur';
            const p = savedPlans.find((x) => String(x.id) === String(v));
            return p ? planLabel(p) : 'Paket seç';
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline': { borderRadius: '16px' },
          }}
          MenuProps={{
            disableAutoFocusItem: true,
            PaperProps: {
              sx: {
                maxHeight: { xs: 320, sm: 420, md: 520 }, // ✅ açılır menü yüksekliği
                overflowY: 'auto', // ✅ scroll
                borderRadius: '16px',
              },
            },
            // (opsiyonel) menü aşağı doğru açılsın
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}
        >
          <MenuItem value="">
            <MDTypography variant="button" sx={labelSx}>
              Paket seç
            </MDTypography>
          </MenuItem>

          {savedPlans.map((p) => (
            <MenuItem key={p.id} value={String(p.id)}>
              <MDBox
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                    {p.name}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: 'text.primary', opacity: 0.65, fontWeight: 600 }}
                  >
                    {money(p?.priceTry)} • Kap: {p?.classCapacity ?? 1}
                  </MDTypography>
                </MDBox>

                <MDBox
                  sx={{ display: 'flex', gap: 0.5 }}
                  onMouseDown={stopSelectMouseDown}
                  onClick={stopSelectClick}
                >
                  <Tooltip title="Düzenle">
                    <IconButton
                      sx={{ p: 0.6, borderRadius: '12px' }}
                      size="small"
                      onMouseDown={stopSelectMouseDown}
                      onClick={(e) => {
                        stopSelectClick(e);
                        openEdit(p.id);
                      }}
                    >
                      <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>edit</Icon>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Pasife al">
                    <IconButton
                      size="small"
                      sx={{ p: 0.6, borderRadius: '12px' }}
                      onMouseDown={stopSelectMouseDown}
                      onClick={(e) => {
                        stopSelectClick(e);
                        askDeactivate(p);
                      }}
                    >
                      <Icon sx={{ fontSize: 18, color: theme.palette.error.main }}>block</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            </MenuItem>
          ))}

          <Divider sx={{ my: 0.5 }} />

          <MenuItem value={CUSTOM_VALUE}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>auto_fix_high</Icon>
              <MDBox>
                <MDTypography fontWeight="bold">Özel Plan</MDTypography>
                <MDTypography variant="caption" sx={labelSx}>
                  Üyeye özel plan oluştur (listeye kaydetmez)
                </MDTypography>
              </MDBox>
            </MDBox>
          </MenuItem>

          <MenuItem value={CREATE_VALUE}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ fontSize: 18, color: theme.palette.success.main }}>add_circle</Icon>
              <MDBox>
                <MDTypography fontWeight="bold">Plan Oluştur</MDTypography>
                <MDTypography variant="caption" sx={labelSx}>
                  Yeni plan kaydet ve seç
                </MDTypography>
              </MDBox>
            </MDBox>
          </MenuItem>
        </MDSelect>

        {/* ✅ Paket / Özel Plan / Snapshot Detayı */}
        {selectedSummary ? (
          <MDBox sx={summaryStyle}>
            <MDTypography variant="caption" fontWeight="bold" sx={{ mb: 0.75, display: 'block' }}>
              {selectedSummary.type === 'CUSTOM'
                ? 'Özel Plan Detayı'
                : selectedSummary.type === 'SNAPSHOT'
                ? 'Mevcut Paket Detayı'
                : 'Paket Detayı'}
            </MDTypography>

            {summaryLine('Ad', selectedSummary.plan?.name || '—')}
            {summaryLine('Fiyat', money(selectedSummary.plan?.priceTry))}
            {summaryLine('Kapasite', String(selectedSummary.plan?.classCapacity ?? 1))}

            <Divider sx={{ my: 0.75 }} />

            <MDBox sx={{ display: 'grid', gap: 0.5 }}>
              {(Array.isArray(selectedSummary.plan?.items) ? selectedSummary.plan.items : [])
                .slice(0, 3)
                .map((it, i) => (
                  <MDTypography key={i} variant="caption" sx={labelSx}>
                    • {itemLabel(it)}
                  </MDTypography>
                ))}

              {Array.isArray(selectedSummary.plan?.items) &&
              selectedSummary.plan.items.length > 3 ? (
                <MDTypography variant="caption" sx={labelSx}>
                  +{selectedSummary.plan.items.length - 3} kalem daha…
                </MDTypography>
              ) : null}
            </MDBox>
          </MDBox>
        ) : null}

        {/* ✅ Anlaşılan Fiyat */}
        {/* ✅ Anlaşılan Fiyat (paket seçiliyse) */}
        {hasSelectedPlan && !hideAgreedPrice ? (
          <MDBox sx={{ display: 'grid', gap: 0.75 }}>
            <MDTypography variant="caption" sx={labelSx}>
              Anlaşılan Fiyat (₺)
            </MDTypography>

            <MDInput
              type="number"
              placeholder="0"
              value={agreedPriceTry ?? ''}
              onChange={(e) => {
                setTouchedPrice(true);
                onChangeAgreedPriceTry?.(e.target.value);
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '999px',
                  height: { xs: 40, sm: 44 },
                  background: 'rgba(255,255,255,.78)',
                  backdropFilter: 'blur(6px)',
                  '& fieldset': { borderColor: 'rgba(0,0,0,.14)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,0,0,.26)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(0,150,136,.55)' },
                },
                '& input': {
                  fontWeight: 900,
                  color: 'text.primary',
                  fontSize: { xs: 14, sm: 15 },
                  padding: '10px 14px',
                },
              }}
            />

            <MDBox
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              <MDBox
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.25,
                  py: 0.7,
                  borderRadius: '999px',
                  border: '1px solid rgba(0,0,0,.10)',
                  background: 'rgba(0,0,0,.03)',
                  width: 'fit-content',
                }}
              >
                <MDTypography variant="caption" sx={labelSx}>
                  Paket
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.primary', fontWeight: 900 }}>
                  {money(packagePriceTry)}
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.7,
                  px: 1.25,
                  py: 0.7,
                  borderRadius: '999px',
                  border: '1px solid rgba(0,0,0,.10)',
                  background:
                    discountPct == null
                      ? 'rgba(0,0,0,.03)'
                      : discountPct > 0
                      ? 'linear-gradient(180deg, rgba(76,175,80,.12), rgba(76,175,80,.03))'
                      : discountPct < 0
                      ? 'linear-gradient(180deg, rgba(251,140,0,.14), rgba(251,140,0,.03))'
                      : 'rgba(0,0,0,.03)',
                  justifySelf: { sm: 'end' },
                  width: 'fit-content',
                }}
              >
                <MDTypography variant="caption" sx={labelSx}>
                  {discountPct == null
                    ? 'İndirim'
                    : discountPct > 0
                    ? 'İndirim'
                    : discountPct < 0
                    ? 'Zam'
                    : 'Durum'}
                </MDTypography>

                <MDTypography
                  variant="caption"
                  sx={{
                    fontWeight: 950,
                    color:
                      discountPct == null
                        ? 'text.primary'
                        : discountPct > 0
                        ? 'success.main'
                        : discountPct < 0
                        ? 'warning.main'
                        : 'text.primary',
                  }}
                >
                  {discountPct == null
                    ? '—'
                    : discountPct > 0
                    ? `%${discountPct}`
                    : discountPct < 0
                    ? `%${Math.abs(discountPct)}`
                    : 'Yok'}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        ) : null}
      </MDBox>

      {/* Builder Dialog */}
      <Dialog
        open={builderOpen}
        onClose={closeBuilder}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: softDialogPaper }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon
                sx={{
                  fontSize: 20,
                  color:
                    builderMode === 'CUSTOM'
                      ? theme.palette.warning.main
                      : theme.palette.success.main,
                }}
              >
                construction
              </Icon>
              <MDTypography fontWeight="bold">
                {builderMode === 'CUSTOM'
                  ? 'Özel Plan'
                  : builderMode === 'EDIT'
                  ? 'Planı Düzenle'
                  : 'Plan Oluştur'}
              </MDTypography>
            </MDBox>

            <IconButton size="small" sx={{ p: 0.6, borderRadius: '12px' }} onClick={closeBuilder}>
              <Icon sx={{ color: theme.palette.text.secondary }}>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <PlanBuilder
            mode={builderMode}
            initialPlan={
              builderMode === 'EDIT'
                ? savedPlans.find((p) => String(p.id) === String(editingPlanId))
                : null
            }
            editingPlanId={editingPlanId}
            activities={activeActivities}
            onCreateActivity={async (name) => {
              await acts.create({ name });
              await acts.fetchList?.();
            }}
            onUpdateActivity={async (id, patch) => {
              await acts.update?.(id, patch);
              await acts.fetchList?.();
            }}
            onDeleteActivity={async (id) => {
              await acts.setActive?.(id, false);
              await acts.fetchList?.();
            }}
            onSave={async (payload) => {
              const fixed = {
                ...payload,
                priceTry: payload.priceTry ?? 0,
                classCapacity: payload.classCapacity ?? 1,
              };
              const created = await plans.create(fixed);
              await plans.fetchList();
              return created;
            }}
            onUpdate={async (planId, payload) => {
              const fixed = {
                ...payload,
                priceTry: payload.priceTry ?? 0,
                classCapacity: payload.classCapacity ?? 1,
              };
              const updated = await plans.update(planId, fixed);
              await plans.fetchList();
              return updated;
            }}
            onDone={handleBuilderDone}
            onCloseBuilder={closeBuilder}
          />
        </DialogContent>
      </Dialog>

      {/* Confirm Pasife Al */}
      <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '18px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>warning</Icon>
            <MDTypography fontWeight="bold">Emin misin?</MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDTypography variant="button" sx={labelSx}>
            <b>{pendingPlan?.name || 'Bu planı'}</b> pasife almak istiyor musun?
          </MDTypography>

          <MDBox display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
            <MDButton
              variant="outlined"
              color="dark"
              sx={{ borderRadius: '12px' }}
              onClick={closeConfirm}
              disabled={confirmBusy}
            >
              Vazgeç
            </MDButton>

            <MDButton
              variant="gradient"
              color="warning"
              sx={{ borderRadius: '12px' }}
              onClick={doDeactivate}
              disabled={confirmBusy}
            >
              <Icon sx={{ mr: 0.5, fontSize: 18 }}>block</Icon>
              Pasife Al
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </FieldCard>
  );
}
